import { decoratorsJson } from "@/components/suggestions/providers/data";
import { checkNodeCondition } from "@/components/suggestions/providers/utils/nodeCondition";
import { setNodeDecorators } from "@/components/suggestions/utils";
import { autoGroup } from "@/input/utils/editOperators";

import type {
  DecoratorJson,
  DecoratorTypeValue,
} from "@/components/suggestions/providers/data/types";
import type { GroupNodeModel } from "@/models/nodes/GroupNodeModel";
import type { NodeModel } from "@/models/nodes/types";

export type DecoratorData = DecoratorJson & { decoratorType: DecoratorTypeValue };

const allDecoratorTypes = Object.keys(decoratorsJson) as DecoratorTypeValue[];

export const ACCENT_DECORATORS: DecoratorTypeValue[] = [
  "vector",
  "hat",
  "check",
  "bar",
  "breve",
  "tilde",
  "ring",
];

export const DOT_DECORATORS: DecoratorTypeValue[] = [
  "dot",
  "double-dot",
  "triple-dot",
  "quadruple-dot",
];

export const CANCEL_DECORATORS: DecoratorTypeValue[] = ["cancel", "bcancel", "xcancel"];

function isDecoratorAvailable(node: NodeModel, decoratorType: DecoratorTypeValue) {
  const { filterRules } = decoratorsJson[decoratorType];

  if (!filterRules) {
    return false;
  }

  return filterRules.some(({ condition }) => {
    return (
      condition.nodeBefore &&
      checkNodeCondition(condition.nodeBefore, node, { skipDecoratorsCondition: true })
    );
  });
}

export function getAvailableDecorators(
  node: NodeModel,
  decoratorTypes?: DecoratorTypeValue[],
): DecoratorData[] {
  return (decoratorTypes ?? allDecoratorTypes)
    .filter((decoratorType) => isDecoratorAvailable(node, decoratorType))
    .map((decoratorType) => ({ decoratorType, ...decoratorsJson[decoratorType] }));
}

export function applyDecorator(node: NodeModel, decoratorType: DecoratorTypeValue) {
  const { doc } = node;
  const decorator = decoratorsJson[decoratorType];
  const autoGroupCondition = decorator.substitution?.autoGroup;

  const groupsSelectedNode =
    !!autoGroupCondition &&
    doc.selection.isSelected(node) &&
    checkNodeCondition(autoGroupCondition.nodeBefore, node);

  if (groupsSelectedNode) {
    doc.selection.resetSelection();
    doc.cursor.moveAfterNode(node);
  }

  doc.transact(() => {
    let group: GroupNodeModel | undefined;

    if (autoGroupCondition) {
      group = autoGroup(doc, autoGroupCondition);
    }

    if ((group?.decorators ?? []).length === 0) {
      setNodeDecorators(doc, node, decorator.substitution?.insertDecorators);
    }

    // focus input after new decorator is rendered
    queueMicrotask(() => {
      doc.focus();
    });
  }, "decoratorsMenu:click");
}
